import React, { useState, useEffect, useCallback } from 'react';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

import ContentHeader from '../../components/_commons/ContentHeader';
import CommonWidget from '../../components/services/dashboard/CommonWidget';
import RecentServices from '../../components/services/dashboard/RecentServices';
import CSMSHealth from '../../components/services/dashboard/CSMSHealth';
import Favorites from '../../components/services/dashboard/Favorites';
import BlogRecentPosts from '../../components/services/dashboard/BlogRecentPosts';
import VODUsage from '../../components/services/dashboard/VODUsage';
import VODConversionStatus from '../../components/services/dashboard/VODConversionStatus';
import widgetData from '@resources/assets/json/widgetData.json';
import layoutData from '@resources/assets/layoutData';
import '@resources/assets/styles/dashboard.scss';

function DashBoard() {
    const [showWidgetList, setShowWidgetList] = useState(false);
    const [usedData, setUsedData] = useState([]);
    const [unusedData, setUnusedData] = useState([]);
    const [isOpen, setIsOpen] = useState(false); // 추가: 모달 열기 상태

    const toggleModal = () => {
        setIsOpen(!isOpen); // 모달 열기/닫기 토글
    };

    // This is a safe way to update unusedData based on usedData changes
    const updateUnusedData = useCallback((newUsedData) => {
        // Don't run this function during initial render
        if (!layoutData) return;

        // Calculate unusedData based on the new usedData
        setUnusedData(prevUnusedData => {
            const unusedWidgets = {
                lg: [],
                md: [],
                sm: [],
                xs: [],
                xxs: []
            };

            // 각 breakpoint를 순회하며 사용되지 않은 위젯을 찾음
            Object.keys(layoutData).forEach(breakpoint => {
                // 각 breakpoint의 모든 위젯
                const allWidgets = layoutData[breakpoint] || [];
                // 현재 사용 중인 위젯 ID 목록
                const usedWidgetIds = newUsedData[breakpoint]?.map(widget => widget.i) || [];

                // 사용되지 않은 위젯 필터링
                unusedWidgets[breakpoint] = allWidgets.filter(widget =>
                    !usedWidgetIds.includes(widget.i)
                );
            });

            return unusedWidgets;
        });
    }, []);

    // Safe wrapper to update both usedData and unusedData
    const updateUsedData = useCallback((newUsedData) => {
        setUsedData(newUsedData);
        updateUnusedData(newUsedData);
    }, [updateUnusedData]);

    // Initialize data on component mount
    useEffect(() => {
        if (layoutData) {
            // 사용할 위젯 키 배열
            const usedWidgetKeys = ['recentServices', 'csmsHealth', 'favorites'];

            // 브레이크포인트별로 구분된 객체 초기화
            const userData = {
                lg: [],
                md: [],
                sm: [],
                xs: [],
                xxs: []
            };

            // 모든 레이아웃 데이터(lg, md, sm, xs, xxs)를 순회
            Object.keys(layoutData).forEach(breakpoint => {
                // 각 breakpoint의 위젯 배열 순회
                layoutData[breakpoint].forEach(widget => {
                    // widget의 i 속성이 usedWidgetKeys에 포함되어 있는지 확인
                    if (usedWidgetKeys.includes(widget.i)) {
                        userData[breakpoint].push(widget);
                    }
                });
            });

            // Set initial state - unusedData will be calculated in the useEffect following this state update
            setUsedData(userData);
        }
    }, []);

    // Calculate unusedData whenever usedData changes
    useEffect(() => {
        if (layoutData && Object.keys(usedData).length > 0) {
            updateUnusedData(usedData);
        }
    }, [usedData, updateUnusedData]);

    const ToolBoxItem = ({ item }) => {
        const onDragStart = (e) => {
            e.dataTransfer.setData("text/plain", JSON.stringify(item));
        };
        return (
            <div
                key={item.id}
                draggable={true}
                onDragStart={(e) => onDragStart(e)}
                className="toolbox-item"
                style={{
                }}
            >
                <div style={{ padding: 5, display: 'flex', justifyContent: 'space-between' }}>
                    <DragIndicatorIcon className="dragHandle" style={{ width: 25, height: 25, cursor: 'move' }} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', padding: 10 }}>
                    <RenderWidget componentName={item.i} />
                </div>
            </div>
        );
    };

    const RenderWidget = ({ componentName }) => {
        const lowerCaseComponentName = componentName.toLowerCase();
        switch (lowerCaseComponentName) {
            case 'recentservices':
                return <RecentServices />;
            case 'csmshealth':
                return <CSMSHealth />;
            case 'favorites':
                return <Favorites />;
            case 'blogrecentposts':
                return <BlogRecentPosts />;
            case 'vodusage':
                return <VODUsage />;
            case 'vodconversionstatus':
                return <VODConversionStatus />;
            default:
                return <div>Unknown Widget: {componentName}</div>;
        }
    };

    const toggleWidgetList = () => {
        setShowWidgetList(!showWidgetList);
    };

    return (
        <div
            className="dashboard-container wrapper-content"
        >
            <div style={{ width: showWidgetList ? '70%' : '100%', height: '100%' }}>
                <div
                    id="content-header"
                    className="dashboard-header content-header"
                >
                    <ContentHeader />
                    <div className="dashboard-buttons">
                        <Button variant="contained" color="secondary" onClick={toggleWidgetList}>
                            <Typography variant="body2">+ 위젯 설정</Typography>
                        </Button>
                    </div>
                </div>

                <div className="content-body">
                    <CommonWidget
                        showWidgetList={showWidgetList}
                        usedData={usedData}
                        setUsedData={updateUsedData}
                    />
                </div>
            </div>

            {showWidgetList && (
                <div
                    className="widget-list"
                    style={{
                        width: '30%',
                        borderLeft: '1px solid #ddd',
                    }}
                >
                    <div
                        style={{
                            padding: '30px',
                            fontSize: '20px',
                            fontWeight: 'bold',
                            display: 'flex',
                            justifyContent: 'space-between',
                        }}
                    >
                        <div>
                        <Typography variant="h6">추가할 위젯 목록</Typography>
                        </div>

                        <div style={{ cursor: 'pointer' }} onClick={toggleWidgetList}>x</div>
                    </div>
                        <div>
                        <Button variant="contained" color="primary" style={{ marginRight: '10px' }}>
                            <Typography variant="body2">기본 레이아웃으로 설정</Typography>
                        </Button>
                        </div>                    
                    {
                        unusedData.lg && unusedData.lg.length > 0 ?
                            <div className="toolbox-items">
                                {unusedData.lg.map(item => (
                                    <ToolBoxItem
                                        key={item.i}
                                        item={item}
                                    />
                                ))}
                            </div>
                            :
                            <div
                                style={{
                                    width: '100%',
                                    textAlign: "center",
                                    padding: 30,
                                    paddingTop: 100,
                                }}
                            >
                                <Typography variant="h6" style={{ marginBottom: 20, fontWeight: "bold" }}>
                                    추가할 위젯이 없습니다
                                </Typography>
                                <Typography variant="body2" style={{ color: "gray", marginBottom: 15 }}>
                                    모든 사용 가능한 위젯이 추가되었습니다.
                                </Typography>
                                <Button variant="contained" color="primary">
                                    위젯 설정 가기
                                </Button>
                            </div>
                    }
                </div>
            )}
        </div>
    );
}

export default DashBoard;